// Helpers to convert between admin-friendly text inputs and the structured
// content shapes the public pages expect.

export const slugify = (s = "") =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Merge built-in items with CMS overrides. Overrides win by slug (edited built-ins),
// original order preserved, genuinely-new custom items appended at the end.
export const dedupeBySlug = (base = [], overrides = []) => {
  const ovBySlug = new Map((overrides || []).filter((x) => x && x.slug).map((x) => [x.slug, x]));
  const baseSlugs = new Set((base || []).filter((x) => x && x.slug).map((x) => x.slug));
  const merged = (base || []).map((b) => (b && b.slug && ovBySlug.has(b.slug) ? ovBySlug.get(b.slug) : b));
  const newOnly = (overrides || []).filter((x) => x && x.slug && !baseSlugs.has(x.slug));
  return [...merged, ...newOnly];
};

export const linesToArray = (text = "") =>
  text.split("\n").map((s) => s.trim()).filter(Boolean);
export const arrayToLines = (arr) => (arr || []).join("\n");

export const csvToArray = (text = "") =>
  text.split(",").map((s) => s.trim()).filter(Boolean);
export const arrayToCsv = (arr) => (arr || []).join(", ");

const pipeRow = (line) => line.split("|").map((s) => s.trim());

// highlights: "Title | Description" per line
export const linesToHighlights = (text) =>
  linesToArray(text).map((l) => {
    const [t, ...d] = pipeRow(l);
    return { title: t || "", desc: d.join(" | ") };
  });
export const highlightsToLines = (arr) =>
  (arr || []).map((h) => `${h.title} | ${h.desc}`).join("\n");

// bestTime: "Season | Label | Note" per line
export const linesToBestTime = (text) =>
  linesToArray(text).map((l) => {
    const [s, lab, ...n] = pipeRow(l);
    return { season: s || "", label: lab || "", note: n.join(" | ") };
  });
export const bestTimeToLines = (arr) =>
  (arr || []).map((b) => `${b.season} | ${b.label} | ${b.note}`).join("\n");

// destination itineraries: "Name | Days | Stop1; Stop2; Stop3" per line
export const linesToItineraries = (text) =>
  linesToArray(text).map((l) => {
    const [n, d, r] = pipeRow(l);
    return { name: n || "", days: d || "", route: (r || "").split(";").map((s) => s.trim()).filter(Boolean) };
  });
export const itinerariesToLines = (arr) =>
  (arr || []).map((it) => `${it.name} | ${it.days} | ${(it.route || []).join("; ")}`).join("\n");

// package day-by-day: "Title | Description" per line -> [{day,title,desc}]
export const linesToDays = (text) =>
  linesToArray(text).map((l, i) => {
    const [t, ...d] = pipeRow(l);
    return { day: i + 1, title: t || "", desc: d.join(" | ") };
  });
export const daysToLines = (arr) =>
  (arr || []).map((d) => `${d.title} | ${d.desc}`).join("\n");

// journal body: markdown-lite -> blocks. "## " heading, "- " list item, else paragraph.
export const textToBody = (text = "") => {
  const blocks = [];
  let listBuf = [];
  const flush = () => {
    if (listBuf.length) { blocks.push({ type: "list", items: listBuf }); listBuf = []; }
  };
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line) { flush(); continue; }
    if (line.startsWith("## ")) { flush(); blocks.push({ type: "h", text: line.slice(3).trim() }); }
    else if (line.startsWith("- ")) { listBuf.push(line.slice(2).trim()); }
    else { flush(); blocks.push({ type: "p", text: line }); }
  }
  flush();
  return blocks;
};
export const bodyToText = (blocks) =>
  (blocks || [])
    .map((b) => {
      if (b.type === "h") return `## ${b.text}`;
      if (b.type === "list") return (b.items || []).map((i) => `- ${i}`).join("\n");
      return b.text;
    })
    .join("\n\n");
